import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, LogIn } from "lucide-react";
import { LINKS, IMAGES } from "@/lib/assets";
import { useSettings } from "@/lib/SettingsContext";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.4 + i * 0.15 },
  }),
};

export const Hero = () => {
  const CONTENT = useSettings();
  const LINE_1 = CONTENT.hero.titleLine1;
  const LINE_2 = CONTENT.hero.titleLine2;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.6]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
        <img src={IMAGES.hero} alt="Semi truck on the open highway" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0b1e40] via-[#0b1e40]/40 to-transparent"
      />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#0b1e40]/70 via-[#0b1e40]/20 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-screen flex flex-col justify-end pb-20 pt-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-xs sm:text-sm tracking-[0.3em] uppercase text-blue-200 font-bold mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-blue-300/60" /> {CONTENT.hero.overline}
        </motion.p>

        <h1 className="font-display font-black text-white uppercase tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl">
          {[LINE_1, LINE_2].map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "text-white" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-200 leading-relaxed"
        >
          {CONTENT.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={LINKS.signup}
            className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors"
            data-testid="hero-join-btn"
          >
            {CONTENT.hero.primaryCta}
            <ArrowUpRight size={20} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href={LINKS.login}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md border border-white/20 text-white font-semibold backdrop-blur-sm hover:border-amber-400 hover:text-amber-400 transition-colors"
            data-testid="hero-login-btn"
          >
            <LogIn size={18} /> {CONTENT.hero.secondaryCta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
